import React, { useState } from 'react'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Delete from '@mui/icons-material/Delete'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Public from '@mui/icons-material/Public'
import PublicOff from '@mui/icons-material/PublicOff'
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked'
import {
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Collapse,
  Box,
  Divider,
  Skeleton,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import {
  useGetProjectTemplateVersions,
  useDeleteProjectTemplateVersion,
  useUpdateProjectTemplateVersion,
} from '@/service-library/hooks/project-template-versions'
import {
  useGetProjectTemplates,
  useUpdateProjectTemplate,
  useCreateProjectTemplateVersion,
} from '@/service-library/hooks/project-templates'
import { ProjectTemplateVersion } from '@/types/project-templates'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import LargeHeading from '../large-heading/LargeHeading'
import ProjectBreadcrumb from '../project-tables/ProjectBreadcrumb'
import { useProjectContext } from '../project-tables/ProjectProvider'
import DeleteProjectTemplateVersionDialog from './DeleteProjectTemplateVersionDialog'

const ProjectTemplateSettings: React.FC = () => {
  const { project } = useProjectContext()
  const [deletingVersionId, setDeletingVersionId] = useState<string | null>(
    null,
  )
  const [settingCurrentVersionId, setSettingCurrentVersionId] = useState<
    string | null
  >(null)
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(
    new Set(),
  )
  const [isCreatingVersion, setIsCreatingVersion] = useState<boolean>(false)
  const deleteOverlay = useOverlay()

  const queryClient = useQueryClient()

  // Get the project template for this project
  const {
    projectTemplates,
    isPending: isLoadingTemplates,
    queryKey: projectTemplatesQueryKey,
  } = useGetProjectTemplates({
    filters: {
      project_id: project.id,
      limit: '1',
    },
  })

  const projectTemplate = projectTemplates?.[0]

  // Get all versions for this template
  const {
    projectTemplateVersions,
    isPending: isLoadingVersions,
    queryKey: projectTemplateVersionsQueryKey,
  } = useGetProjectTemplateVersions({
    filters: {
      project_template_id: projectTemplate?.id || '',
      limit: '100',
    },
    enabled: !!projectTemplate?.id,
  })

  const { updateProjectTemplate } = useUpdateProjectTemplate({
    sideEffectQueryKeys: [projectTemplatesQueryKey],
    onSuccess: () => {
      showSuccessSnackbar('Current version updated successfully')
      setSettingCurrentVersionId(null)
    },
    onError: () => {
      showErrorSnackbar('Failed to update current version. Please try again.')
      setSettingCurrentVersionId(null)
    },
  })

  const { deleteProjectTemplateVersion } = useDeleteProjectTemplateVersion({
    listQueryKey: projectTemplateVersionsQueryKey,
    onSuccess: () => {
      showSuccessSnackbar('Template version deleted successfully')
      setDeletingVersionId(null)
      deleteOverlay.close()
    },
    onError: () => {
      showErrorSnackbar('Failed to delete template version. Please try again.')
      setDeletingVersionId(null)
    },
  })

  const { updateProjectTemplateVersion } = useUpdateProjectTemplateVersion({
    sideEffectQueryKeys: [projectTemplateVersionsQueryKey],
    onSuccess: () => {
      showSuccessSnackbar('Visibility updated successfully')
    },
    onError: () => {
      showErrorSnackbar('Failed to update visibility. Please try again.')
    },
  })

  const { createProjectTemplateVersion } = useCreateProjectTemplateVersion({
    onSuccess: () => {
      showSuccessSnackbar('New Version Created')
      setIsCreatingVersion(false)

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: projectTemplateVersionsQueryKey,
        })
      }, 3000)
    },
    onError: () => {
      showErrorSnackbar(
        'Failed to create new template version. Please try again.',
      )
      setIsCreatingVersion(false)
    },
  })

  function handleCreateNewVersion() {
    if (!projectTemplate) return
    setIsCreatingVersion(true)
    createProjectTemplateVersion({
      template_id: projectTemplate.id,
    })
  }

  function handleSetCurrentVersion(versionId: string) {
    if (!projectTemplate) return
    setSettingCurrentVersionId(versionId)
    updateProjectTemplate({
      ...projectTemplate,
      current_version_id: versionId,
    })
  }

  function handleDeleteVersion(version: ProjectTemplateVersion) {
    setDeletingVersionId(version.id)
    deleteProjectTemplateVersion(version.id)
  }

  function toggleVersionExpansion(versionId: string) {
    setExpandedVersions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(versionId)) {
        newSet.delete(versionId)
      } else {
        newSet.add(versionId)
      }
      return newSet
    })
  }

  const currentVersion = projectTemplateVersions?.find(
    (version) => version.id === projectTemplate?.current_version_id,
  )

  const sortedVersions = projectTemplateVersions?.sort((a, b) => {
    // Sort by version array - assuming it's [major, minor, patch]
    for (let i = 0; i < Math.max(a.version.length, b.version.length); i++) {
      const aVal = a.version[i] || 0
      const bVal = b.version[i] || 0
      if (aVal !== bVal) {
        return bVal - aVal // Descending order (newest first)
      }
    }
    return 0
  })

  if (isLoadingTemplates) {
    return (
      <Container maxWidth="md">
        <Stack alignItems="center" sx={{ p: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading template...</Typography>
        </Stack>
      </Container>
    )
  }

  if (!projectTemplate) {
    return (
      <Container maxWidth="md">
        <ProjectBreadcrumb label="Template" url="../template" />
        <Alert severity="info" sx={{ mt: 2 }}>
          This project is not a template project. Only projects that have been
          saved as templates will show template settings here.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="md">
      <ProjectBreadcrumb label="Template" url="../template" />

      <LargeHeading
        heading="Template Settings"
        subHeading={`Managing versions for "${projectTemplate.project_display_name}"`}
      />

      <Stack spacing={3} sx={{ mt: 3 }}>
        {/* Current Version Info */}
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Current Version</Typography>
              {currentVersion ? (
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Chip
                    icon={<CheckCircle />}
                    label={`v${currentVersion.version.join('.')}`}
                    color="primary"
                    variant="filled"
                  />
                  <Typography color="text.secondary">
                    This is the version used when creating new projects from
                    this template.
                  </Typography>
                </Stack>
              ) : (
                <div>
                  <Skeleton width={200} />
                  <Skeleton width={100} />
                </div>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Template Versions List */}
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6">All Versions</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={isCreatingVersion || isLoadingVersions}
                  onClick={handleCreateNewVersion}
                  startIcon={
                    isCreatingVersion ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                >
                  {isCreatingVersion ? 'Creating...' : 'Create New Version'}
                </Button>
              </Stack>

              {isLoadingVersions ? (
                <Stack alignItems="center" sx={{ p: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading versions...
                  </Typography>
                </Stack>
              ) : !sortedVersions?.length ? (
                <Alert severity="info">No template versions found.</Alert>
              ) : (
                <Stack spacing={1}>
                  {sortedVersions.map((version) => {
                    const isCurrentVersion =
                      version.id === projectTemplate.current_version_id
                    const isDeleting = deletingVersionId === version.id
                    const isSettingCurrent =
                      settingCurrentVersionId === version.id
                    const canDelete =
                      sortedVersions.length > 1 && !isCurrentVersion

                    const isExpanded = expandedVersions.has(version.id)

                    return (
                      <Card key={version.id} variant="outlined">
                        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                          <Stack>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Tooltip title="View template JSON">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      toggleVersionExpansion(version.id)
                                    }
                                  >
                                    {isExpanded ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Typography variant="h6">
                                  v{version.version.join('.')}
                                </Typography>
                                {isCurrentVersion && (
                                  <Chip
                                    icon={<CheckCircle />}
                                    label="Current"
                                    color="primary"
                                    size="small"
                                  />
                                )}
                              </Stack>

                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Tooltip
                                  title={
                                    version.is_public
                                      ? 'Public - click to make private'
                                      : 'Private - click to make public'
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      updateProjectTemplateVersion({
                                        ...version,
                                        is_public: !version.is_public,
                                      })
                                    }
                                  >
                                    {version.is_public ? (
                                      <Public color="primary" />
                                    ) : (
                                      <PublicOff color="action" />
                                    )}
                                  </IconButton>
                                </Tooltip>

                                {!isCurrentVersion && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                      isSettingCurrent ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <RadioButtonUnchecked />
                                      )
                                    }
                                    disabled={isSettingCurrent || isDeleting}
                                    onClick={() =>
                                      handleSetCurrentVersion(version.id)
                                    }
                                  >
                                    {isSettingCurrent
                                      ? 'Setting...'
                                      : 'Set as Current'}
                                  </Button>
                                )}

                                <Tooltip
                                  title={
                                    canDelete
                                      ? 'Delete this version'
                                      : sortedVersions.length === 1
                                      ? 'Cannot delete the only version'
                                      : 'Cannot delete the current version'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      color="error"
                                      size="small"
                                      disabled={
                                        !canDelete ||
                                        isDeleting ||
                                        isSettingCurrent
                                      }
                                      onClick={() => {
                                        deleteOverlay.open()
                                        setDeletingVersionId(version.id)
                                      }}
                                    >
                                      {isDeleting ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <Delete />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </Stack>

                            <Collapse in={isExpanded}>
                              <Box>
                                <Divider sx={{ my: 2 }} />
                                <Stack spacing={1}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Template JSON:
                                  </Typography>
                                  <Box
                                    sx={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #e9ecef',
                                      borderRadius: 1,
                                      p: 2,
                                      overflow: 'auto',
                                      maxHeight: 800,
                                    }}
                                  >
                                    <Box
                                      component="pre"
                                      sx={{
                                        margin: 0,
                                        fontSize: '0.875rem',
                                        fontFamily:
                                          'Consolas, Monaco, "Courier New", monospace',
                                        whiteSpace: 'pre-wrap',
                                        color: '#212529',
                                        backgroundColor: 'transparent',
                                      }}
                                    >
                                      {JSON.stringify(
                                        version.template,
                                        null,
                                        2,
                                      )}
                                    </Box>
                                  </Box>
                                </Stack>
                              </Box>
                            </Collapse>
                          </Stack>
                        </CardContent>
                      </Card>
                    )
                  })}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Delete Confirmation Dialog */}
      <DeleteProjectTemplateVersionDialog
        overlay={deleteOverlay}
        version={
          sortedVersions?.find((v) => v.id === deletingVersionId) || null
        }
        onDelete={() => {
          const version = sortedVersions?.find(
            (v) => v.id === deletingVersionId,
          )
          if (version) {
            handleDeleteVersion(version)
          }
        }}
        onCancel={() => {
          setDeletingVersionId(null)
          deleteOverlay.close()
        }}
      />
    </Container>
  )
}

export default ProjectTemplateSettings
